import { z } from 'zod';
import type { DataScope } from '@crm/permissions';
import { CUSTOMER_SITUATIONS, type PersonRef, type UnitRef } from '../customers/contract';
import type { BusinessHours, Weekday } from '../settings/units/business-hours';
import type { SlaState } from './sla-contract';
import { CHANNEL_TYPES, type ChannelType } from './contract';
import type { TriageOutcome } from './receptionist-contract';

/**
 * Queues and distribution (docs/specs/009-filas-e-distribuicao/spec.md, §3 and §6): the shapes
 * the API answers, the zod schemas of what it accepts and the pure rules both sides share
 * (who manages a queue, business hours, the routing conditions). The distribution engine
 * itself runs on the server (`src/mocks/conversations/distribution/engine.ts` in the mocked API).
 */

/* ------------------------------------------------------------------ constants */

export const AVAILABILITY_STATUSES = ['online', 'away', 'busy', 'offline'] as const;
export type AvailabilityStatus = (typeof AVAILABILITY_STATUSES)[number];

export const DISTRIBUTION_STRATEGIES = ['round_robin', 'least_busy', 'manual'] as const;
export type DistributionStrategy = (typeof DISTRIBUTION_STRATEGIES)[number];

export const ABSENCE_POLICIES = ['wait', 'queue'] as const;
export type AbsencePolicy = (typeof ABSENCE_POLICIES)[number];

/** Whether resolving a conversation of the queue asks for an outcome (QA-31). */
export const OUTCOME_ON_RESOLVE = ['off', 'optional', 'required'] as const;
export type OutcomeOnResolve = (typeof OUTCOME_ON_RESOLVE)[number];

/** Spec 048: what the queue is for; decides which sales and service settings apply. */
export const QUEUE_KINDS = ['sales', 'service'] as const;
export type QueueKind = (typeof QUEUE_KINDS)[number];

/** Whether resolving a conversation of the queue asks for a contact reason (spec 046). */
export const REASON_ON_RESOLVE = ['off', 'optional', 'required'] as const;
export type ReasonOnResolve = (typeof REASON_ON_RESOLVE)[number];

/** When the queue sends the contact the protocol of the case (spec 046). */
export const PROTOCOL_MESSAGE = ['off', 'on_open', 'on_resolve', 'both'] as const;
export type ProtocolMessage = (typeof PROTOCOL_MESSAGE)[number];

export const DEFAULT_PROTOCOL_TEXT = 'Seu atendimento tem o protocolo {{atendimento.protocolo}}.';
export const PROTOCOL_TEXT_MAX = 500;

/** Why the engine gave a conversation to someone (rule 21). */
export const ASSIGNMENT_REASONS = [
  'owner',
  'continuity',
  'rule',
  'round-robin',
  'least-busy',
] as const;
export type AssignmentReason = (typeof ASSIGNMENT_REASONS)[number];

/** Why a conversation waits in its queue (rules 15, 16, 19 and 20). */
export const QUEUED_REASONS = ['waiting-owner', 'no-agent', 'manual', 'outside-hours'] as const;
export type QueuedReason = (typeof QUEUED_REASONS)[number];

export const RULE_FIELDS = [
  'channel',
  'connection',
  'unit',
  'customer-unit',
  'customer-tag',
  'customer-situation',
  'deal-pipeline',
  'deal-stage',
  'custom-field',
  'keyword',
  'time-of-day',
] as const;
export type RuleField = (typeof RULE_FIELDS)[number];

/** `customer-situation` also matches a contact without a customer. */
export const RULE_SITUATIONS = [...CUSTOMER_SITUATIONS, 'unknown'] as const;
export type RuleSituation = (typeof RULE_SITUATIONS)[number];

export const RULE_TARGET_TYPES = ['queue', 'team', 'member'] as const;
export type RuleTargetType = (typeof RULE_TARGET_TYPES)[number];

export const QUEUE_LIMITS = {
  nameMin: 2,
  nameMax: 60,
  ruleNameMax: 60,
  rulesMax: 20,
  conditionsMax: 10,
  conditionValueMax: 120,
  capacityMax: 200,
  waitMinutesMin: 1,
  waitMinutesMax: 240,
  continuityDaysMax: 90,
  autoAwayMinutesMax: 240,
  transferNoteMax: 4096,
} as const;

export const DEFAULT_DISTRIBUTION_SETTINGS: DistributionSettings = {
  continuityDays: 7,
  absenceWaitMinutes: 10,
  autoAwayMinutes: 15,
  backgroundTabCountsAsActive: false,
};

/** How often the page tells the API the person is active (rule 10). */
export const HEARTBEAT_INTERVAL_MS = 60_000;

/** Codes of the errors of this contract (translated in `errors.api.<CODE>`). */
export const DISTRIBUTION_ERROR_CODES = [
  'QUEUE_NOT_FOUND',
  'QUEUE_INVALID',
  'DISTRIBUTION_SETTINGS_INVALID',
  'AVAILABILITY_INVALID',
  'QUEUE_NAME_TAKEN',
  'QUEUE_CONNECTION_TAKEN',
  'CONVERSATION_TRANSFER_FORBIDDEN',
  'QUEUE_OUT_OF_REACH',
] as const;

/* ------------------------------------------------------------------ shapes */

export type RuleCondition =
  | { field: 'channel'; value: ChannelType }
  | {
      field: 'connection' | 'unit' | 'customer-unit' | 'deal-pipeline' | 'deal-stage';
      value: string;
    }
  | { field: 'customer-tag' | 'keyword'; value: string }
  | { field: 'customer-situation'; value: RuleSituation }
  | { field: 'custom-field'; key: string; value: string }
  | { field: 'time-of-day'; from: string; to: string };

export type RuleTarget =
  | { type: 'queue'; queueId: string }
  | { type: 'team'; teamId: string }
  | { type: 'member'; membershipId: string };

export interface RoutingRule {
  id: string;
  name: string;
  conditions: RuleCondition[];
  target: RuleTarget;
}

export interface QueueAbsence {
  policy: AbsencePolicy;
  /** null: the organization's default (`absenceWaitMinutes`). */
  waitMinutes: number | null;
}

export interface Queue {
  id: string;
  name: string;
  unit: UnitRef | null;
  active: boolean;
  connections: { id: string; name: string }[];
  members: PersonRef[];
  teams: { id: string; name: string }[];
  strategy: DistributionStrategy;
  /** null: no limit. */
  maxOpenPerAgent: number | null;
  onlyDuringBusinessHours: boolean;
  rules: RoutingRule[];
  absence: QueueAbsence;
  fallbackAlert: boolean;
  /** What the queue is for (spec 048): a sales queue makes leads; a service queue, cases. */
  kind: QueueKind;
  /** Whether resolving one of its conversations asks for an outcome (QA-31). */
  outcomeOnResolve: OutcomeOnResolve;
  /** Whether resolving one of its conversations asks for a contact reason (spec 046). */
  reasonOnResolve: ReasonOnResolve;
  /** When the contact gets the protocol of the case, and with which text (spec 046). */
  protocolMessage: ProtocolMessage;
  protocolText: string;
  /** New contacts whose conversation lands here become leads (spec 031). */
  createsLeads: boolean;
  /** The caller attends in it (and gets its conversations). */
  mine: boolean;
  /**
   * Its folder shows in the inbox: attended, or supervised by the caller (absent reads as `mine`).
   */
  inbox?: boolean;
  can: { manage: boolean };
  createdAt: string;
  updatedAt: string;
}

export interface DistributionSettings {
  continuityDays: number;
  absenceWaitMinutes: number;
  /** 0: never away by inactivity. */
  autoAwayMinutes: number;
  /** A page in a background tab still counts as activity. */
  backgroundTabCountsAsActive: boolean;
}

export interface AgentAvailability {
  membershipId: string;
  name: string;
  status: AvailabilityStatus;
  /** Away because of inactivity (rule 10). */
  auto: boolean;
  since: string | null;
  /** Open and pending conversations assigned (rule 12). */
  openCount: number;
  unitIds: string[];
  /** The page keeps signalling activity from a background tab (organization setting). */
  backgroundTabCountsAsActive: boolean;
}

export interface ConversationDistribution {
  state: 'queued';
  reason: QueuedReason;
  reservedFor: PersonRef | null;
  reservedUntil: string | null;
}

/** The detail of a distribution or transfer system message (rules 21 and 25). */
export interface SystemDetail {
  reason: AssignmentReason | QueuedReason | null;
  queue: { id: string; name: string } | null;
  team: { id: string; name: string } | null;
  rule: string | null;
  until: string | null;
  /** The outcome chosen when the conversation was resolved (QA-31). */
  outcome?: { id: string; label: string } | null;
  /** The connection a member started the conversation on (spec 015). */
  connection?: { id: string; name: string } | null;
  /** What happened in a WhatsApp group: who, and its new name (spec 024, rule 13). */
  group?: { people: string[]; subject: string | null } | null;
  /** How a pass through the receptionist ended, and the option chosen (spec 023). */
  triage?: { outcome: TriageOutcome; option: string | null; flow?: string | null } | null;
}

export interface SupervisedConversation {
  id: string;
  contactName: string;
  assignee: PersonRef | null;
  status: 'open' | 'pending';
  waitingSince: string | null;
  lastMessageAt: string;
  distribution: ConversationDistribution | null;
  /** The SLA of the conversation (spec 010, rule 27). */
  sla: { state: SlaState; dueAt: string | null } | null;
  /**
   * The caller was mentioned in a note here: `unread` while the latest mention is newer than
   * their last read, `read` after, `null` never mentioned.
   */
  mention: 'unread' | 'read' | null;
}

export interface SupervisedAgent {
  membershipId: string;
  name: string;
  status: AvailabilityStatus;
  auto: boolean;
  openCount: number;
}

export interface QueueSupervision {
  queue: {
    id: string;
    name: string;
    unit: UnitRef | null;
    strategy: DistributionStrategy;
    maxOpenPerAgent: number | null;
  };
  waiting: number;
  oldestWaitingSince: string | null;
  /** The queue's active conversations at risk and breached (spec 010, rule 27). */
  sla: { atRisk: number; breached: number };
  conversations: SupervisedConversation[];
  agents: SupervisedAgent[];
}

export interface Supervision {
  queues: QueueSupervision[];
}

/* ------------------------------------------------------------------ realtime */

/** The realtime events of this epic (spec §5), as the web reads them. */
export const distributionEventSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('availability.updated'),
    data: z.object({ membershipId: z.string() }),
  }),
  z.object({ type: z.literal('queue.updated'), data: z.object({ queueId: z.string() }) }),
]);

/* ------------------------------------------------------------------ schemas */

const id = z.string().min(1);
export const TIME_OF_DAY_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const time = z.string().regex(TIME_OF_DAY_PATTERN);
const conditionText = z.string().trim().min(1).max(QUEUE_LIMITS.conditionValueMax);

export const ruleConditionSchema = z.discriminatedUnion('field', [
  z.object({ field: z.literal('channel'), value: z.enum(CHANNEL_TYPES) }).strict(),
  z.object({ field: z.literal('connection'), value: id }).strict(),
  z.object({ field: z.literal('unit'), value: id }).strict(),
  z.object({ field: z.literal('customer-unit'), value: id }).strict(),
  z.object({ field: z.literal('deal-pipeline'), value: id }).strict(),
  z.object({ field: z.literal('deal-stage'), value: id }).strict(),
  z.object({ field: z.literal('customer-tag'), value: conditionText }).strict(),
  z.object({ field: z.literal('keyword'), value: conditionText }).strict(),
  z.object({ field: z.literal('customer-situation'), value: z.enum(RULE_SITUATIONS) }).strict(),
  z
    .object({ field: z.literal('custom-field'), key: z.string().min(1), value: conditionText })
    .strict(),
  z.object({ field: z.literal('time-of-day'), from: time, to: time }).strict(),
]);

export const ruleTargetSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('queue'), queueId: id }).strict(),
  z.object({ type: z.literal('team'), teamId: id }).strict(),
  z.object({ type: z.literal('member'), membershipId: id }).strict(),
]);

export const routingRuleInputSchema = z
  .object({
    name: z.string().trim().min(1).max(QUEUE_LIMITS.ruleNameMax),
    conditions: z.array(ruleConditionSchema).min(1).max(QUEUE_LIMITS.conditionsMax),
    target: ruleTargetSchema,
  })
  .strict();
export type RoutingRuleInput = z.input<typeof routingRuleInputSchema>;

const queueFields = {
  name: z.string().trim().min(QUEUE_LIMITS.nameMin).max(QUEUE_LIMITS.nameMax),
  unitId: id.nullable(),
  active: z.boolean(),
  connectionIds: z.array(id).max(50),
  memberIds: z.array(id).max(500),
  teamIds: z.array(id).max(100),
  strategy: z.enum(DISTRIBUTION_STRATEGIES),
  maxOpenPerAgent: z.number().int().min(1).max(QUEUE_LIMITS.capacityMax).nullable(),
  onlyDuringBusinessHours: z.boolean(),
  rules: z.array(routingRuleInputSchema).max(QUEUE_LIMITS.rulesMax),
  absence: z
    .object({
      policy: z.enum(ABSENCE_POLICIES),
      waitMinutes: z
        .number()
        .int()
        .min(QUEUE_LIMITS.waitMinutesMin)
        .max(QUEUE_LIMITS.waitMinutesMax)
        .nullable(),
    })
    .strict(),
  fallbackAlert: z.boolean(),
  /** Optional on create (`sales`): spec 048. */
  kind: z.enum(QUEUE_KINDS).optional(),
  /** Optional on create (`off`). */
  outcomeOnResolve: z.enum(OUTCOME_ON_RESOLVE).optional(),
  /** Optional on create (`off`, `off`, the default text): spec 046. */
  reasonOnResolve: z.enum(REASON_ON_RESOLVE).optional(),
  protocolMessage: z.enum(PROTOCOL_MESSAGE).optional(),
  protocolText: z.string().trim().min(1).max(PROTOCOL_TEXT_MAX).optional(),
  /** Optional on create (on): whether new contacts here become leads (spec 031). */
  createsLeads: z.boolean().optional(),
};

export const queueInputSchema = z.object(queueFields).strict();
export type QueueInput = z.input<typeof queueInputSchema>;

export const updateQueueSchema = z.object(queueFields).partial().strict();
export type UpdateQueueInput = z.input<typeof updateQueueSchema>;

export const distributionSettingsSchema = z
  .object({
    continuityDays: z.number().int().min(0).max(QUEUE_LIMITS.continuityDaysMax),
    absenceWaitMinutes: z
      .number()
      .int()
      .min(QUEUE_LIMITS.waitMinutesMin)
      .max(QUEUE_LIMITS.waitMinutesMax),
    autoAwayMinutes: z.number().int().min(0).max(QUEUE_LIMITS.autoAwayMinutesMax),
    backgroundTabCountsAsActive: z.boolean(),
  })
  .strict();

export const availabilityInputSchema = z.object({ status: z.enum(AVAILABILITY_STATUSES) }).strict();

export const transferConversationSchema = z
  .object({
    target: ruleTargetSchema,
    note: z.string().trim().max(QUEUE_LIMITS.transferNoteMax).optional(),
  })
  .strict();
export type TransferConversationInput = z.input<typeof transferConversationSchema>;

/* ------------------------------------------------------------------ rules */

/** Rule 4: who may change a queue — the organization scope all, the others their units'. */
export function canManageQueue(
  caller: {
    permissions: readonly string[];
    dataScope: DataScope;
    unitIds: readonly string[];
  },
  queue: { unitId: string | null },
): boolean {
  if (!caller.permissions.includes('core.queue.manage')) return false;
  if (caller.dataScope === 'organization') return true;
  return queue.unitId !== null && caller.unitIds.includes(queue.unitId);
}

const WEEKDAY_KEYS: readonly Weekday[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const minutesOfTime = (value: string, end = false) => {
  if (end && value === '24:00') return 24 * 60;
  const [hours = 0, minutes = 0] = value.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Rule 15: whether the unit is open at a wall-clock moment (`weekday` 0 = Sunday, `minutes`
 * since midnight, both in the organization's zone). Unknown hours count as always open.
 */
export function isWithinBusinessHours(
  hours: BusinessHours | null | undefined,
  moment: { weekday: number; minutes: number },
): boolean {
  if (!hours) return true;
  const day = WEEKDAY_KEYS[moment.weekday];
  if (!day) return true;
  return hours[day].some(
    (interval) =>
      minutesOfTime(interval.start) <= moment.minutes &&
      moment.minutes < minutesOfTime(interval.end, true),
  );
}

/** Rule 7: `from`–`to` in minutes; `from` after `to` crosses midnight. */
export function isWithinTimeOfDay(from: string, to: string, minutes: number): boolean {
  const start = minutesOfTime(from);
  const end = minutesOfTime(to);
  if (start === end) return true;
  return start < end ? minutes >= start && minutes < end : minutes >= start || minutes < end;
}

const fold = (text: string) =>
  text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();

/** Rule 7, `keyword`: any of the comma-separated words appears in the text. */
export function matchesKeywords(keywords: string, text: string): boolean {
  const haystack = fold(text);
  return keywords
    .split(',')
    .map(fold)
    .filter(Boolean)
    .some((word) => haystack.includes(word));
}

/** Text comparison of the conditions: no case nor accents. */
export function sameText(a: string, b: string): boolean {
  return fold(a) === fold(b);
}
